/**

 * Google Drive Source Registration Form

 */



import React, { useState } from 'react';

import { Button } from '../UI/Button';

import { Input } from '../UI/Input';

import { registerGDriveSource } from '../../api/sources';

import { GoogleDriveBrowser } from './GoogleDriveBrowser';



interface GDriveFormProps {

  projectId: string;

  onSuccess: () => void;

  onClose: () => void;

}



type SyncFrequency = 'hourly' | '6hourly' | 'daily' | 'weekly';



export function GDriveForm({ projectId, onSuccess, onClose }: GDriveFormProps) {

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const [gdriveName, setGdriveName] = useState('');

  const [gdriveDescription, setGdriveDescription] = useState('');

  const [gdriveServiceAccountJson, setGdriveServiceAccountJson] = useState('');

  const [gdriveSharedDriveId, setGdriveSharedDriveId] = useState('');

  const [gdriveFolderId, setGdriveFolderId] = useState('');

  const [selectedFolderLabel, setSelectedFolderLabel] = useState('');

  const [gdriveSyncFrequency, setGdriveSyncFrequency] = useState<SyncFrequency>('weekly');

  const [gdriveIncludeShared, setGdriveIncludeShared] = useState(false);

  const [showAdvanced, setShowAdvanced] = useState(false);



  const handleSubmit = async () => {

    setError(null);

    setLoading(true);



    try {

      if (!gdriveName || !gdriveServiceAccountJson) {

        setError('Name and service account JSON are required for backend sync');

        setLoading(false);

        return;

      }



      if (!gdriveFolderId) {

        setError('Select a Google Drive folder to sync');

        setLoading(false);

        return;

      }



      await registerGDriveSource({

        project_id: projectId,

        name: gdriveName,

        description: gdriveDescription || undefined,

        credentials: {

          service_account_json: gdriveServiceAccountJson,

          shared_drive_id: gdriveSharedDriveId || undefined,

        },

        folder_id: gdriveFolderId || undefined,

        watch_for_changes: true,

        recursive: true,

        sync_frequency: gdriveSyncFrequency,

        include_shared_with_me: gdriveIncludeShared,

      });



      onSuccess();

      onClose();

    } catch (err: any) {

      setError(err.response?.data?.detail || err.message || 'Failed to register Google Drive source');

      setLoading(false);

    }

  };



  return (

    <div className="space-y-4">

      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">

        <p className="text-sm text-blue-900 dark:text-blue-100">

          Connect Google Drive, browse to a folder, then provide the service account JSON TraceOS uses to sync.

          Share the selected folder with the service account email before the first sync.

        </p>

      </div>



      <div>

        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">

          Source Name *

        </label>

        <Input

          value={gdriveName}

          onChange={(e) => setGdriveName(e.target.value)}

          placeholder="e.g., SPARC Proposals, Company Context"

        />

      </div>



      <div>

        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">

          Google Drive Folder *

        </label>

        <GoogleDriveBrowser

          selectedId={gdriveFolderId || undefined}

          selectedLabel={selectedFolderLabel || undefined}

          onSelect={(selection) => {

            setGdriveFolderId(selection.id);

            setSelectedFolderLabel(selection.name);

            if (!gdriveName.trim()) {

              setGdriveName(selection.name);

            }

          }}

        />

      </div>



      <div>

        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">

          Sync Frequency

        </label>

        <select

          value={gdriveSyncFrequency}

          onChange={(e) => setGdriveSyncFrequency(e.target.value as SyncFrequency)}

          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800"

        >

          <option value="weekly">Weekly</option>

          <option value="daily">Daily</option>

          <option value="6hourly">Every 6 Hours</option>

          <option value="hourly">Hourly</option>

        </select>

      </div>



      <div className="flex items-center gap-2">

        <input

          type="checkbox"

          id="gdrive-shared"

          checked={gdriveIncludeShared}

          onChange={(e) => setGdriveIncludeShared(e.target.checked)}

          className="rounded"

        />

        <label htmlFor="gdrive-shared" className="text-sm text-gray-700 dark:text-gray-300">

          Include files shared with service account

        </label>

      </div>



      <div>

        <button

          type="button"

          className="text-sm text-blue-600 dark:text-blue-400 hover:underline"

          onClick={() => setShowAdvanced((v) => !v)}

        >

          {showAdvanced ? 'Hide' : 'Show'} advanced options

        </button>

        {showAdvanced && (

          <div className="mt-3 space-y-4 rounded-lg border border-gray-200 dark:border-slate-600 p-3">

            <div>

              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">

                Service Account JSON *

              </label>

              <textarea

                value={gdriveServiceAccountJson}

                onChange={(e) => setGdriveServiceAccountJson(e.target.value)}

                placeholder="Paste service account JSON credentials here..."

                rows={8}

                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg font-mono text-xs bg-white dark:bg-slate-800"

              />

              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">

                Required for TraceOS sync worker. Share the picked folder with this service account email.

              </p>

            </div>



            <div>

              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">

                Shared Drive ID (optional)

              </label>

              <Input

                value={gdriveSharedDriveId}

                onChange={(e) => setGdriveSharedDriveId(e.target.value)}

                placeholder="Only if syncing a Shared Drive root (Team Drive)"

              />

            </div>



            <div>

              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">

                Folder ID (manual override)

              </label>

              <Input

                value={gdriveFolderId}

                onChange={(e) => {

                  setGdriveFolderId(e.target.value);

                  if (e.target.value) {

                    setSelectedFolderLabel('');

                  }

                }}

                placeholder="Populated by browse, or paste folder ID manually"

              />

            </div>

          </div>

        )}

      </div>



      <div>

        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">

          Description

        </label>

        <Input

          value={gdriveDescription}

          onChange={(e) => setGdriveDescription(e.target.value)}

          placeholder="Optional description"

        />

      </div>



      {error && (

        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">

          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>

        </div>

      )}



      <div className="flex gap-2 pt-2">

        <Button variant="outline" onClick={onClose} disabled={loading}>

          Cancel

        </Button>

        <Button onClick={handleSubmit} disabled={loading}>

          {loading ? 'Registering...' : 'Register Source'}

        </Button>

      </div>

    </div>

  );

}


